import React, { useEffect, useRef } from "react"

interface JitsiMeetingProps {
  roomName: string
  userDisplayName: string
  password?: string
  width?: string
  height?: string
}

declare global {
  interface Window {
    JitsiMeetExternalAPI: any
  }
}

export function JitsiMeeting({ 
  roomName, 
  userDisplayName, 
  password,
  width = "100%",
  height = "500px"
}: JitsiMeetingProps) {
  const jitsiContainerRef = useRef<HTMLDivElement>(null)
  const jitsiApiRef = useRef<any>(null)

  useEffect(() => {
    if (!jitsiContainerRef.current) return

    // Load Jitsi Meet API script if not already loaded
    const loadJitsiScript = () => {
      return new Promise((resolve, reject) => {
        if (window.JitsiMeetExternalAPI) {
          resolve(window.JitsiMeetExternalAPI)
          return
        }

        const script = document.createElement("script")
        script.src = "https://meet.jit.si/external_api.js"
        script.async = true
        script.onload = () => resolve(window.JitsiMeetExternalAPI)
        script.onerror = reject
        document.head.appendChild(script)
      })
    }

    loadJitsiScript()
      .then(() => {
        if (jitsiApiRef.current) {
          jitsiApiRef.current.dispose()
        }

        const options = {
          roomName: roomName,
          width: width,
          height: height,
          parentNode: jitsiContainerRef.current,
          configOverwrite: {
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            enableWelcomePage: false,
            prejoinPageEnabled: true,
            hideConferenceTimer: false,
            enableClosePage: false
          },
          interfaceConfigOverwrite: {
            TOOLBAR_BUTTONS: [
              'microphone', 'camera', 'closedcaptions', 'desktop', 'fullscreen',
              'fodeviceselection', 'hangup', 'profile', 'chat', 'recording',
              'livestreaming', 'etherpad', 'sharedvideo', 'settings', 'raisehand',
              'videoquality', 'filmstrip', 'invite', 'feedback', 'stats', 'shortcuts',
              'tileview', 'videobackgroundblur', 'download', 'help', 'mute-everyone',
              'security'
            ],
            SETTINGS_SECTIONS: ['devices', 'language', 'moderator', 'profile', 'calendar'],
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            SHOW_PROMOTIONAL_CLOSE_PAGE: false,
            SHOW_CHROME_EXTENSION_BANNER: false
          },
          userInfo: {
            displayName: userDisplayName
          }
        }

        // Add password if provided
        if (password) {
          (options.configOverwrite as any).roomPassword = password
        }

        jitsiApiRef.current = new window.JitsiMeetExternalAPI("meet.jit.si", options)

        // Event listeners
        jitsiApiRef.current.addEventListener("participantJoined", (participant: any) => {
          console.log("Participant joined:", participant)
        })

        jitsiApiRef.current.addEventListener("participantLeft", (participant: any) => {
          console.log("Participant left:", participant)
        })

        jitsiApiRef.current.addEventListener("videoConferenceJoined", () => {
          console.log("User joined the conference")
        })

        jitsiApiRef.current.addEventListener("videoConferenceLeft", () => {
          console.log("User left the conference")
        })
      })
      .catch((error) => {
        console.error("Failed to load Jitsi Meet API:", error)
      })

    // Cleanup
    return () => {
      if (jitsiApiRef.current) {
        jitsiApiRef.current.dispose()
        jitsiApiRef.current = null
      }
    }
  }, [roomName, userDisplayName, password, width, height])

  return (
    <div 
      ref={jitsiContainerRef}
      style={{ width, height }}
      className="rounded-lg overflow-hidden border"
    />
  )
}